/**
 * Impersonation must show the *target* tenant, never the actor's leftover pin.
 * Viewing Eco-Luxe then Priya Harbor used to keep Eco-Luxe in the dashboard chrome
 * because request_org_id() / user_workspace_context lagged the identity switch.
 */

import { supabase } from '../../lib/supabase';
import { getImpersonationOverride, setImpersonationOverride } from '../../utils/userImpersonationOverride';
import { getActiveWorkspaceOrgId, setActiveWorkspaceOrgId } from './activeWorkspaceOrgId';
import { fetchMyOrg, fetchOrgById, setWorkspaceOrg, type SaasOrg } from './saasOrg';

export function pickImpersonationWorkspaceOrgId(input: {
  overrideOrgId?: string | null;
  targetMembershipOrgId?: string | null;
}): string | null {
  return input.overrideOrgId?.trim() || input.targetMembershipOrgId?.trim() || null;
}

function membershipRoleRank(role?: string | null): number {
  if (role === 'super_admin' || role === 'owner') return 0;
  if (role === 'admin') return 1;
  return 2;
}

/** Primary tenant for a user — membership first, then profiles.organization_id. */
export async function fetchUserPrimaryOrgId(userId: string): Promise<string | null> {
  const id = userId.trim();
  if (!id) return null;

  const { data: memberships } = await supabase
    .from('organization_members')
    .select('organization_id, role, created_at')
    .eq('user_id', id)
    .eq('status', 'active');

  const rows = (memberships ?? []) as {
    organization_id: string;
    role?: string | null;
    created_at?: string | null;
  }[];
  if (rows.length) {
    rows.sort((a, b) => {
      const rankDiff = membershipRoleRank(a.role) - membershipRoleRank(b.role);
      if (rankDiff !== 0) return rankDiff;
      return new Date(b.created_at ?? 0).getTime() - new Date(a.created_at ?? 0).getTime();
    });
    return rows[0]?.organization_id ?? null;
  }

  const { data: profile } = await supabase
    .from('profiles')
    .select('organization_id')
    .eq('id', id)
    .maybeSingle();
  return typeof profile?.organization_id === 'string' ? profile.organization_id : null;
}

export async function resolveImpersonationWorkspaceOrgId(
  actorUserId?: string | null,
): Promise<string | null> {
  let userId = actorUserId?.trim() || null;
  if (!userId) {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    userId = session?.user?.id ?? null;
  }
  if (!userId) return null;

  const override = getImpersonationOverride(userId);
  if (!override?.target?.id) return null;

  const resolved = pickImpersonationWorkspaceOrgId({
    overrideOrgId: override.workspaceOrgId,
    targetMembershipOrgId: override.workspaceOrgId ? null : await fetchUserPrimaryOrgId(override.target.id),
  });
  if (resolved && !override.workspaceOrgId) {
    setImpersonationOverride(userId, override.target, {
      workspaceOrgId: resolved,
      actorIsPlatformAdmin: override.actorIsPlatformAdmin,
      viewAsRole: override.viewAsRole,
      actorHomeOrgId: override.actorHomeOrgId,
    });
  }
  return resolved;
}

/** Pin RLS + in-memory workspace to the impersonated tenant. Never keeps a leftover actor pin. */
export async function pinImpersonationWorkspace(actorUserId?: string | null): Promise<string | null> {
  const orgId = await resolveImpersonationWorkspaceOrgId(actorUserId);
  if (!orgId) return null;
  setActiveWorkspaceOrgId(orgId);
  try {
    await setWorkspaceOrg(orgId);
  } catch (err) {
    console.warn('pinImpersonationWorkspace:', err);
  }
  return orgId;
}

export async function fetchImpersonationWorkspaceOrg(actorUserId?: string | null): Promise<SaasOrg | null> {
  const orgId = await pinImpersonationWorkspace(actorUserId);
  if (!orgId) return null;
  return fetchOrgById(orgId);
}

/** After exit, put the platform admin back on a membership org — not Eco-Luxe leftover. */
export async function restoreActorWorkspaceOrg(
  _actorUserId: string,
  homeOrgId?: string | null,
): Promise<string | null> {
  const preferred = homeOrgId?.trim() || (await fetchMyOrg())?.id || getActiveWorkspaceOrgId();
  if (!preferred) {
    setActiveWorkspaceOrgId(null);
    return null;
  }
  setActiveWorkspaceOrgId(preferred);
  try {
    await setWorkspaceOrg(preferred);
  } catch (err) {
    console.warn('restoreActorWorkspaceOrg:', err);
  }
  return preferred;
}
